import { ICategory } from '@Hotels-at-Home/hah-fe-core/dist/types/category';
import { find, get } from 'lodash';
import { IBaseProduct } from '@Hotels-at-Home/hah-fe-core/dist/types';
import { INTL_SITES } from '~/constants/siteConfig';

export const homepageHrefLangTags = [
  {
    hrefLang: 'de-de',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/de`,
  },
  {
    hrefLang: 'fr-fr',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/fr`,
  },
  {
    hrefLang: 'en-us',
    href: 'https://na.sofitelboutique.com/',
  },
  {
    hrefLang: 'fr-us',
    href: 'https://na.sofitelboutique.com/fr/',
  },
  {
    hrefLang: 'fr-ca',
    href: 'https://na.sofitelboutique.com/fr/',
  },
  {
    hrefLang: 'en-gb',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
  {
    hrefLang: 'en-ie',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
  {
    hrefLang: 'en-ca',
    href: 'https://na.sofitelboutique.com/',
  },
  {
    hrefLang: 'es-es',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/es`,
  },
  {
    hrefLang: 'en',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
  {
    hrefLang: 'es',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/es`,
  },
  {
    hrefLang: 'fr',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/fr`,
  },
  {
    hrefLang: 'de',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/de`,
  },
  {
    hrefLang: 'zh-cn',
    href: 'https://www.shopsofitel.cn/zh-CN/',
  },
  {
    hrefLang: 'en-cn',
    href: 'https://www.shopsofitel.cn/en/',
  },
  {
    hrefLang: 'zh-en',
    href: 'https://www.shopsofitel.cn/en/',
  },
  {
    hrefLang: 'x-default',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
];

export const hrefOrder = [
  'en-us',
  'fr-us',
  'fr-ca',
  'en-ie',
  'en-gb',
  'en-ca',
  'en',
  'fr',
  'fr-fr',
  'de',
  'de-de',
  'es',
  'es-es',
  'zh-cn',
  'en-cn',
  'x-default',
];

interface Map {
  [key: string]: string | undefined;
}

export interface HrefLink {
  region: string;
  links: LinkItem[];
}

export interface LinkItem {
  value: string;
  url: string;
  icon?: string;
}

export interface HrefMap {
  href: string;
  hreflang?: string;
  rel: string;
}

export const filterExternalHrefData = (hrefData: HrefMap[]): HrefMap[] => {
  const filteredHrefData = hrefData.filter(
    (item: HrefMap) =>
      item.hreflang !== 'en-ie' &&
      item.hreflang !== 'en-gb' &&
      item.hreflang !== 'en' &&
      item.hreflang !== 'fr-fr' &&
      item.hreflang !== 'de-de' &&
      item.hreflang !== 'es-es' &&
      item.hreflang !== 'x-default',
  );

  // Find the value for "us-fr"
  const usFrHref = filteredHrefData.find(item => item.hreflang === 'fr-us')?.href;

  // Check if "fr-ca" is already defined
  const frCaExists = filteredHrefData.some(item => item.hreflang === 'fr-ca');

  // If "us-fr" exists and "fr-ca" is not defined, add "ca-fr" with the same value
  if (usFrHref && !frCaExists) {
    filteredHrefData.push({
      href: usFrHref,
      hreflang: 'fr-ca',
      rel: 'alternate',
    });
  }

  // Find the value for "us-en"
  const usEnHref = filteredHrefData.find(item => item.hreflang === 'en-us')?.href;

  // Check if "en-ca" is already defined
  const enCaExists = filteredHrefData.some(item => item.hreflang === 'en-ca');

  // If "us-en" exists and "en-ca" is not defined, add "ca-en" with the same value
  if (usEnHref && !enCaExists) {
    filteredHrefData.push({
      href: usEnHref,
      hreflang: 'en-ca',
      rel: 'alternate',
    });
  }

  return filteredHrefData;
};

export const generateEuHrefData = (
  languageUrls: { href: string; value: string }[],
  siteUrl: string,
  pathPrefix: string,
): { href: string; url: string; rel: string }[] => {
  const euHrefData: { href: string; url: string; rel: string }[] = [
    ...languageUrls.map(({ href, value }) => ({
      href,
      url: `${siteUrl}/${href}${pathPrefix}${value}`,
      rel: 'alternate',
    })),
  ];

  const enEuHref = euHrefData?.find(item => item.href === 'en')?.url;
  const frEuHref = euHrefData?.find(item => item.href === 'fr')?.url;
  const deEuHref = euHrefData?.find(item => item.href === 'de')?.url;
  const esEuHref = euHrefData?.find(item => item.href === 'es')?.url;

  euHrefData.push(
    {
      href: 'en-ie',
      url: enEuHref,
      rel: 'alternate',
    },
    {
      href: 'en-gb',
      url: enEuHref,
      rel: 'alternate',
    },
    {
      href: 'fr-fr',
      url: frEuHref,
      rel: 'alternate',
    },
    {
      href: 'de-de',
      url: deEuHref,
      rel: 'alternate',
    },
    {
      href: 'es-es',
      url: esEuHref,
      rel: 'alternate',
    },
    {
      href: 'x-default',
      url: enEuHref,
      rel: 'alternate',
    },
  );

  return euHrefData;
};

export const getHrefData = (
  item: IBaseProduct | ICategory,
  locale: string,
  useFallbacks = false,
) => {
  const hrefLangs: Map = {
    en:
      get(item, 'hreflang_en-us', null) ??
      get(item, 'hreflang_en_us', null) ??
      get(item, 'hreflang_en_US', null) ??
      get(item, 'hreflang_en_us', null),
    'de-de': item?.hreflang_de,
    'en-gb': get(item, 'hreflang_en_ie', null),
    'en-ie': useFallbacks
      ? get(item, 'hreflang_en_ie', null) ??
        get(item, 'hreflang_en', find(homepageHrefLangTags, { hrefLang: 'en' }).href)
      : get(item, 'hreflang_en_ie', null) ?? get(item, 'hreflang_en', null),
    'en-us': useFallbacks
      ? get(item, 'hreflang_en_US', null) ??
        get(item, 'hreflang_en_us', null) ??
        find(homepageHrefLangTags, { hrefLang: 'en-us' }).href
      : get(item, 'hreflang_en_US', null) ?? get(item, 'hreflang_en_us', null),
    'fr-ca': useFallbacks
      ? get(item, 'hreflang_fr_CA', null) ??
        get(item, 'hreflang_fr_ca', null) ??
        get(item, 'hreflang_fr_us', null) ??
        find(homepageHrefLangTags, { hrefLang: 'fr-ca' }).href
      : get(item, 'hreflang_fr_CA', null) ?? get(item, 'hreflang_fr_ca', null),
    'fr-us': useFallbacks
      ? get(item, 'hreflang_fr_US', null) ??
        get(item, 'hreflang_fr_us', null) ??
        find(homepageHrefLangTags, { hrefLang: 'fr-us' }).href
      : get(item, 'hreflang_fr_US', null) ?? get(item, 'hreflang_fr_us', null),
    'en-ca': useFallbacks
      ? get(item, 'hreflang_en_CA', null) ??
        get(item, 'hreflang_en_ca', null) ??
        find(homepageHrefLangTags, { hrefLang: 'en-ca' }).href
      : get(item, 'hreflang_en_CA', null) ?? get(item, 'hreflang_en_ca', null),
    'zh-cn': useFallbacks
      ? get(item, 'hreflang_zh_cn', null) ?? find(homepageHrefLangTags, { hrefLang: 'zh-cn' }).href
      : get(item, 'hreflang_zh_cn', null),
    'en-cn': useFallbacks
      ? get(item, 'hreflang_en_CN', null) ??
        get(item, 'hreflang_en_cn', null) ??
        find(homepageHrefLangTags, { hrefLang: 'en-cn' }).href
      : get(item, 'hreflang_en_CN', null) ?? get(item, 'hreflang_en_cn', null),
    'fr-fr': item?.hreflang_fr,
    'es-es': item?.hreflang_es,
    'x-default': get(item, 'hreflang_en', null) ?? get(item, 'hreflang_x_default', null),
  };

  const hrefData: HrefMap[] = [];

  hrefOrder.forEach(key => {
    if (hrefLangs[key] !== null) {
      hrefData.push({
        href: hrefLangs[key],
        hreflang: key,
        rel: 'alternate',
      });
    }
  });

  return hrefData;
};

export function extractHrefLinks(item: IBaseProduct | ICategory): LinkItem[] {
  const createLink = (link: string | null) => {
    if (!link) return '';
    return link.includes('?') ? `${link}&referrer=eu` : `${link}?referrer=eu`;
  };

  const usLink = createLink(
    get(item, 'hreflang_en_US', null) ??
      get(item, 'hreflang_en_us', null) ??
      find(homepageHrefLangTags, { hrefLang: 'en-us' }).href,
  );

  // NOTE: Adjust for FR CA vs US CA sites (fr-ca vs en-ca)
  const caLink = createLink(
    get(item, 'hreflang_fr_CA', null) ??
      get(item, 'hreflang_fr_ca', null) ??
      get(item, 'hreflang_fr_US', null) ??
      get(item, 'hreflang_fr_us', null) ??
      find(homepageHrefLangTags, { hrefLang: 'fr-ca' }).href,
  );

  return [
    {
      value: 'English',
      url: usLink,
      icon: 'us',
    },
    {
      value: 'Français',
      url: caLink,
      icon: 'ca',
    },
  ];
}

export function getHrefLinks(
  isHomepage: boolean,
  product: IBaseProduct,
  category: ICategory,
): HrefLink[] {
  if (isHomepage) {
    return INTL_SITES;
  }

  const item = product || category;
  const result = item ? extractHrefLinks(item) : [];

  const [naLinkObj, apacLinkObj = { links: [] as LinkItem[] }] = INTL_SITES ?? [];

  return [
    {
      region: 'NA',
      links: result.length ? result : naLinkObj.links,
    },
    {
      region: 'APAC',
      links: apacLinkObj.links,
    },
  ];
}

export const generateHreflangTags = (url: string): { href: string; hreflang: string }[] => {
  const generatedHrefLangs = ['en', 'fr', 'de', 'es'].map(hrefLang => ({
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/${hrefLang}${url}`,
    hreflang: hrefLang,
  }));

  return [
    ...generatedHrefLangs,
    { href: `${process.env.NEXT_PUBLIC_SITE_URL}${url}`, hreflang: 'x-default' },
  ];
};
export const formatHref = (href: string, pathname: string): string => {
  let newHref = href;
  // Remove trailing slash if it exists
  if (newHref.endsWith('/')) {
    newHref = newHref.slice(0, -1);
  }

  const newPathname = pathname.replace('/help', '');
  if (newHref.includes('na')) {
    return `${newHref}${newPathname}.aspx`;
  }

  if (newHref.includes('cn')) {
    return `${newHref}${newPathname}`;
  }

  return `${newHref}${pathname}`;
};
